/**
 * Episode content model — strict mirror of TeaCheck/Domain/EpisodeModels.swift.
 * Decoding is tolerant: unknown keys are ignored, optional fields may be absent.
 * Never add authored content here; content comes from Supabase `episodes.content`.
 */

export type Sender = 'client' | 'player' | 'system';
export type MessageKind = 'text' | 'phoneCopy' | 'voice';
export type ChatGestureDemo = 'proof_long_press';
export type AnswerType = 'proof' | 'text' | 'choice';
export type ValidationType = 'proof_id' | 'aliases' | 'llm_judge';
export type AppDepth = 'deep' | 'shallow';

export interface ChatNarrativeTransition {
  title: string;
  callerName?: string;
  outcome?: string;
  elapsedText: string;
  illustrationAsset?: string;
  eyebrow?: string;
  contactName?: string;
  isConnected?: boolean;
}

export interface ChatMessageContent {
  id: string;
  sender: Sender;
  text: string;
  kind: MessageKind;
  transcript?: string;
  /** Supabase storage path, e.g. `episode_1_same_spot_thursday/chat/audio/x.mp3` (bundled copy served from /media). */
  mediaPath?: string;
  mediaRevision?: number;
  mediaDurationSeconds?: number;
  /** Known demos are typed; unknown ids from newer content still decode. */
  demoID?: ChatGestureDemo | (string & {});
  attachmentID?: string;
  replyOptions?: string[];
  narrativeTransition?: ChatNarrativeTransition;
}

export interface ReplyChoice {
  id: string;
  text: string;
}

export interface ProofDeliverySpec {
  recipientName: string;
  appID: string;
  conversationID: string;
  attachmentID?: string;
  proposalMessage?: string;
  stagingMessages?: string[];
}

export interface ValidationSpec {
  type: ValidationType;
  correctProofIDs: string[];
  closeProofIDs?: string[];
  acceptedAliases: string[];
  closeAliases: string[];
  expectedIdea?: string;
  correctConcepts: string[];
  closeConcepts: string[];
  wrongConcepts: string[];
  preciseRule?: string;
}

export interface ChatBeatContent {
  id: string;
  text: string;
  attachmentID?: string;
  sender?: Sender;
  replyOptions?: string[];
  narrativeTransition?: ChatNarrativeTransition;
}

export interface ReactionSet {
  correct: string;
  correctMessages?: string[];
  correctMessagesByProofID?: Record<string, string[]>;
  /** Replaces the generic correct messages for a choice request. */
  correctMessagesByChoiceID?: Record<string, string[]>;
  /** Appended after the correct messages, keyed by a choice picked on an earlier request. */
  epilogueMessagesByChoiceID?: Record<string, string[]>;
  convergenceMessagesByChoiceID?: Record<string, string[]>;
  close: string;
  closeMessagesByProofID?: Record<string, string>;
  wrong: string;
  wrongVariants?: string[];
  unclear: string;
  correctBeats?: ChatBeatContent[];
  correctBeatsByChoiceID?: Record<string, ChatBeatContent[]>;
}

export interface EpisodeRequest {
  id: string;
  position: number;
  prompt: string;
  answerType: AnswerType;
  choices?: ReplyChoice[];
  validation: ValidationSpec;
  reactions: ReactionSet;
  hints: string[];
  proofDelivery?: ProofDeliverySpec;
}

export type ConversationEntryKind =
  | 'message'
  | 'timestamp'
  | 'attachment'
  | 'typing'
  | 'system'
  | 'remoteErase';
export type ConversationSide = 'owner' | 'peer' | 'system';

export interface PhoneConversationEntry {
  id: string;
  kind: ConversationEntryKind;
  side: ConversationSide;
  text?: string;
  attachmentID?: string;
  animateForRequestID?: string;
  delayMilliseconds?: number;
  speakerName?: string;
}

/** Flat string metadata; keys are app-specific (see docs in features/phone/kit). */
export type ItemMetadata = Record<string, string>;

export interface PhoneItemContent {
  id: string;
  title: string;
  subtitle: string;
  timestamp?: string;
  detail?: string;
  proofID?: string;
  metadata: ItemMetadata;
  conversationEntries?: PhoneConversationEntry[];
}

export interface PhoneSectionContent {
  id: string;
  title: string;
  items: PhoneItemContent[];
}

export interface PhoneAppContent {
  /** Renderer key: messages, instagram, photos, … (see features/phone/apps). */
  id: string;
  name: string;
  iconSystemName: string;
  depth: AppDepth;
  sections: PhoneSectionContent[];
}

export interface RewardContent {
  xp: number;
  badgeProgressText: string;
}

export interface EpisodeClosingPresentation {
  date: string;
  headline: string;
  invitationLabel: string;
  cancellationLabel: string;
  punchlineLines: string[];
}

export interface EpisodeContent {
  id: string;
  slug: string;
  title: string;
  clientName: string;
  copiedPhoneName: string;
  fakePhoneLabel: string;
  copiedPhonePasscode?: string;
  copiedPhonePasscodeHint?: string;
  paywallAfterRequestID: string;
  openingMessages: ChatMessageContent[];
  requests: EpisodeRequest[];
  phoneApps: PhoneAppContent[];
  closingPresentation?: EpisodeClosingPresentation;
  finalSummary: string;
  reward: RewardContent;
  clientAttachments?: PhoneItemContent[];
  conclusionHeadline?: string;
  rewardHeadline?: string;
}

/** Legacy accessors (port of the Swift computed properties). */
export function correctMessageTexts(r: ReactionSet): string[] {
  return r.correctMessages && r.correctMessages.length > 0 ? r.correctMessages : [r.correct];
}
export function wrongProofResponses(r: ReactionSet): string[] {
  return r.wrongVariants && r.wrongVariants.length > 0 ? r.wrongVariants : [r.wrong];
}
export function resolvedMediaRevision(m: { mediaRevision?: number }): number {
  return m.mediaRevision ?? 1;
}
export function isCall(t: ChatNarrativeTransition): boolean {
  return t.callerName != null;
}

/** Minimal structural check used when content arrives from the network. */
export function assertEpisodeContent(value: unknown): EpisodeContent {
  const v = value as Partial<EpisodeContent> | null;
  if (!v || typeof v !== 'object') throw new Error('Episode content is not an object');
  for (const key of ['id', 'slug', 'title', 'clientName', 'copiedPhoneName', 'fakePhoneLabel', 'paywallAfterRequestID', 'finalSummary'] as const) {
    if (typeof v[key] !== 'string') throw new Error(`Episode content missing string field ${key}`);
  }
  if (!Array.isArray(v.openingMessages) || !Array.isArray(v.requests) || !Array.isArray(v.phoneApps)) {
    throw new Error('Episode content missing openingMessages/requests/phoneApps');
  }
  if (!v.reward || typeof v.reward.xp !== 'number') throw new Error('Episode content missing reward');
  return v as EpisodeContent;
}
